import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';

/**
 * A custom element that manages product sharing functionality.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 * @property {HTMLFormElement} form – The form element.
 * @property {HTMLInputElement} yourNameInput – Your name input field.
 * @property {HTMLInputElement} theirNameInput – Their name input field.
 * @property {HTMLButtonElement} whatsappButton – WhatsApp share button.
 * @property {HTMLButtonElement} emailButton – Email share button.
 * @property {HTMLElement} errorMessage – Error message element.
 *
 * @extends Component<Refs>
 */
export class ProductShareComponent extends DialogComponent {
  requiredRefs = ['dialog', 'form', 'yourNameInput', 'theirNameInput', 'whatsappButton', 'emailButton', 'errorMessage'];

  connectedCallback() {
    super.connectedCallback();

    // Add input event listeners to clear errors on typing
    this.refs.yourNameInput.addEventListener('input', () => this.clearError());
    this.refs.theirNameInput.addEventListener('input', () => this.clearError());
  }

  /**
   * Validates the form to ensure both name fields are filled.
   *
   * @returns {boolean} True if form is valid, false otherwise.
   */
  validateForm() {
    const yourName = this.refs.yourNameInput.value.trim();
    const theirName = this.refs.theirNameInput.value.trim();

    if (!yourName || !theirName) {
      this.showError();
      
      if (!yourName) {
        this.refs.yourNameInput.classList.add('error');
      }
      if (!theirName) {
        this.refs.theirNameInput.classList.add('error');
      }

      return false;
    }

    this.clearError();
    return true;
  }

  /**
   * Shows the error message and marks invalid inputs.
   */
  showError() {
    this.refs.errorMessage.style.display = 'block';
  }

  /**
   * Clears the error message and removes error classes from inputs.
   */
  clearError() {
    this.refs.errorMessage.style.display = 'none';
    this.refs.yourNameInput.classList.remove('error');
    this.refs.theirNameInput.classList.remove('error');
  }

  /**
   * Generates the personalized message from the template.
   *
   * @returns {string} The formatted message with all placeholders replaced.
   */
  generateMessage() {
    const yourName = this.refs.yourNameInput.value.trim();
    const theirName = this.refs.theirNameInput.value.trim();
    const productTitle = this.dataset.productTitle || '';
    const productUrl = this.dataset.productUrl || '';
    const actionText = this.dataset.actionText || 'pre-order now';
    
    let message = this.dataset.messageTemplate || '{your_name} has added the {product_title} to their wishlist. fancy treating them? {action_text}. {product_url}';

    // Replace all placeholders
    message = message.replace(/{your_name}/g, yourName);
    message = message.replace(/{their_name}/g, theirName);
    message = message.replace(/{product_title}/g, productTitle);
    message = message.replace(/{product_url}/g, productUrl);
    message = message.replace(/{action_text}/g, actionText);

    return message;
  }

  /**
   * Handles WhatsApp share button click.
   * Detects device and opens appropriate WhatsApp URL.
   *
   * @param {Event} event - The click event.
   */
  shareWhatsApp(event) {
    event.preventDefault();

    if (!this.validateForm()) {
      return;
    }

    const message = this.generateMessage();
    const encodedMessage = encodeURIComponent(message);

    // Detect if mobile device
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth < 768;
    
    // Use appropriate WhatsApp URL
    const baseUrl = isMobile ? 'https://wa.me/' : 'https://web.whatsapp.com/send';
    const whatsappUrl = `${baseUrl}?text=${encodedMessage}`;

    // Open in new window
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  }

  /**
   * Handles Email share button click.
   * Opens default email client with pre-filled subject and body.
   *
   * @param {Event} event - The click event.
   */
  shareEmail(event) {
    event.preventDefault();

    if (!this.validateForm()) {
      return;
    }

    const message = this.generateMessage();
    const productTitle = this.dataset.productTitle || 'this product';
    
    const subject = encodeURIComponent(`Check out ${productTitle}`);
    const body = encodeURIComponent(message);

    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;

    // Open email client
    window.location.href = mailtoUrl;
  }
}

if (!customElements.get('product-share-component')) {
  customElements.define('product-share-component', ProductShareComponent);
}
